import React from 'react'
import { Formik, Form } from 'formik'
import {
  Grid,
  OutlinedInput,
  Typography,
  useTheme,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
  FormHelperText,
  InputAdornment,
  TextField,
} from '@mui/material'
import Label from 'components/Label'
import { ArrowRightOutlined } from '@ant-design/icons'
import { validationSchema } from './validationSchema'

const createStyles = (theme, isCreate) => ({
  container: {
    height: '100%',
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.secondary.lighter,
    borderLeft: isCreate ? 'none' : `1px solid ${theme.palette.secondary.dark}`,
  },
  form: {
    height: isCreate ? '100%' : 'calc(100% - 65px)',
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.secondary.light,
    borderRadius: '4px',
    border: `1px solid ${theme.palette.secondary.dark}`,
    justifyContent: 'flex-start',
  },
  textareaAutosize: {
    borderRadius: '4px',
    border: `1px solid ${theme.palette.secondary.darker}`,
    background: theme.palette.secondary.light,
    boxShadow: `1px 1px 4px 0 ${theme.customShadows.z1}`,
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    fontWeight: '400',
    lineHeight: '1.5rem',
    padding: '6px 45px 6px 12px',
    fontFamily: theme.typography.fontFamily,
    outline: 'none',
    '&:focus': {
      outline: 'none',
    },
    '&:placeholder': {
      color: theme.palette.text.disabled,
    },
  },
  textareaAutosizeHelper: {
    fontSize: '0.875rem',
    fontWeight: '400',
    lineHeight: '1.5rem',
    color: theme.palette.text.primary,
    position: 'absolute',
    top: '25px',
    right: '12px',
  },
  buttonContainer: {
    borderTop: `1px solid ${theme.palette.secondary.darker}`,
    padding: '0.75rem 0.5rem',
    backgroundColor: theme.palette.secondary.light,
    marginLeft: '-0.75rem',
    marginRight: '-0.75rem',
    width: 'calc(100% + 1.5rem)',
    flexBasis: 'auto !important',
    maxWidth: 'calc(100% + 1.5rem) !important',
  },
  buttontText: {
    fontStyle: 'italic',
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  button: {
    '&.Mui-disabled': {
      backgroundColor: theme.palette.secondary.darker,
    },
  },
})

export default function PortfolioForm({
  initialValues,
  onSubmit,
  isSubmitDisabled = false,
  isCreate = false,
  disabled = false,
}) {
  const theme = useTheme()
  const styles = createStyles(theme, isCreate)

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({
        values,
        handleChange,
        setFieldValue,
        errors,
        touched,
        handleBlur,
        isSubmitting,
        isValid,
        dirty,
        resetForm,
      }) => (
        <Form style={styles.container}>
          <Grid container alignContent={'space-between'} height={'100%'}>
            <Grid item xs={12} style={styles.form}>
              <Grid container rowSpacing={2} columnSpacing={2}>
                <Grid item xs={12}>
                  <Stack spacing={0.5}>
                    <Label text={'Portfolio Name'} required>
                      <OutlinedInput
                        required
                        fullWidth
                        id="name"
                        name="name"
                        value={values.name}
                        placeholder="Insert"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.name && Boolean(errors.name)}
                        inputProps={{ maxLength: 50 }}
                        disabled={disabled}
                        endAdornment={
                          <InputAdornment position="end">{`${values.name.length}/50`}</InputAdornment>
                        }
                      />
                    </Label>
                    {touched.name && errors.name && (
                      <FormHelperText error>{errors.name}</FormHelperText>
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={6}>
                  <Stack spacing={0.5}>
                    <Label
                      text={'Type'}
                      required
                      tooltip={
                        <>
                          Internal portfolios are actively managed and accessible via portfolio
                          filters. External portfolios are used for reference and excluded from
                          filtering options.
                        </>
                      }
                    >
                      <ToggleButtonGroup
                        required
                        fullWidth
                        id="type"
                        value={values.isCounterparty}
                        exclusive
                        disabled={disabled}
                        onChange={(_, value) => {
                          if (value !== null) setFieldValue('isCounterparty', value)
                        }}
                      >
                        <ToggleButton value={false}>Internal</ToggleButton>
                        <ToggleButton value={true}>External</ToggleButton>
                      </ToggleButtonGroup>
                    </Label>
                    {touched.isCounterparty && errors.isCounterparty && (
                      <FormHelperText error>{errors.isCounterparty}</FormHelperText>
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={6}></Grid>
                <Grid item xs={12}>
                  <Stack spacing={0.5}>
                    <Label text={'Location'}>
                      <OutlinedInput
                        id="location"
                        name="location"
                        fullWidth
                        value={values.location}
                        placeholder="Insert"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={disabled}
                        inputProps={{ maxLength: 25 }}
                        endAdornment={
                          <InputAdornment position="end">
                            {`${values.location.length}/25`}
                          </InputAdornment>
                        }
                      />
                    </Label>
                    {touched.location && errors.location && (
                      <FormHelperText error>{errors.location}</FormHelperText>
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Stack spacing={0.5} style={{ position: 'relative' }}>
                    <Label text={'Description'}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        id="description"
                        name="description"
                        value={values.description}
                        placeholder="Insert"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={disabled}
                        maxLength={80}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">{`${
                              values.description?.length || 0
                            }/80`}</InputAdornment>
                          ),
                        }}
                      />
                    </Label>
                    {touched.description && errors.description && (
                      <FormHelperText error>{errors.description}</FormHelperText>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Grid>
            {!disabled && (
              <Grid
                item
                xs={12}
                textAlign={'start'}
                mt={isCreate ? 4.5 : 1.5}
                container
                rowSpacing={0.5}
                columnSpacing={1}
                sx={!isCreate ? styles.buttonContainer : undefined}
              >
                {isCreate && (
                  <Grid item xs={12}>
                    <Typography variant={'h6'} style={styles.buttonText}>
                      Create and continue to account mapping
                    </Typography>
                  </Grid>
                )}
                {!isCreate && (
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="secondary"
                      onClick={() => resetForm()}
                      disabled={isSubmitting}
                    >
                      Reset
                    </Button>
                  </Grid>
                )}
                <Grid item xs={isCreate ? 12 : 6}>
                  <Button
                    variant="contained"
                    color="primary"
                    endIcon={isCreate && <ArrowRightOutlined />}
                    type="submit"
                    fullWidth
                    sx={styles.button}
                    disabled={isSubmitDisabled || isSubmitting || !isValid || !dirty}
                  >
                    {isCreate ? 'Create' : 'Save'}
                  </Button>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Form>
      )}
    </Formik>
  )
}
